/**
 * lib/sentry.ts
 *
 * Sentry error tracking configuration.
 * Initializes Sentry with environment-specific settings.
 *
 * To enable Sentry:
 * 1. Create a Sentry project at https://sentry.io
 * 2. Add VITE_SENTRY_DSN to your .env files
 * 3. Sentry will automatically capture errors
 */
import * as Sentry from '@sentry/react';

// Only initialize in browser environment
const isBrowser = typeof window !== 'undefined';

// Environment detection
const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

/**
 * Initialize Sentry error tracking.
 * Should be called once at app startup (in main.tsx).
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Skip initialization if no DSN configured
  if (!dsn) {
    if (isDevelopment) {
      console.info('[Sentry] No DSN configured, error tracking disabled');
    }
    return;
  }

  if (!isBrowser) {
    return;
  }

  Sentry.init({
    dsn,
    environment: isProduction ? 'production' : 'development',

    // Performance monitoring - sample 10% of transactions in production
    tracesSampleRate: isProduction ? 0.1 : 1.0,

    // Session replay - sample 10% of sessions, 100% of error sessions
    replaysSessionSampleRate: isProduction ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,

    // Filter out known non-critical errors
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Ignore network errors that are expected (user offline, etc.)
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          return null;
        }
        if (error.message.includes('NetworkError')) {
          return null;
        }
        // Ignore 401 errors (expected for auth flows)
        if (error.message.includes('401')) {
          return null;
        }
      }

      return event;
    },

    // Don't send errors in development by default
    enabled: isProduction || import.meta.env.VITE_SENTRY_DEBUG === 'true',

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text content for privacy
        maskAllText: true,
        // Block all media for privacy
        blockAllMedia: true,
      }),
    ],
  });

  if (isDevelopment) {
    console.info('[Sentry] Initialized with DSN:', dsn.substring(0, 20) + '...');
  }
}

/**
 * Set user context for Sentry.
 * Call this after successful login.
 */
export function setSentryUser(user: {
  id: string;
  email?: string;
  role?: string;
}): void {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    // Add role as extra context
  });
  Sentry.setTag('user_role', user.role ?? 'unknown');
}

/**
 * Clear user context from Sentry.
 * Call this on logout.
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

/**
 * Capture an exception manually.
 * Use this for errors you catch but still want to track.
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>
): void {
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capture a message (non-error event).
 * Use this for warnings or info you want to track.
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info'
): void {
  Sentry.captureMessage(message, level);
}

/**
 * Add breadcrumb for debugging.
 * Breadcrumbs appear in error reports to show what happened before the error.
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>
): void {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

// Export Sentry for direct access if needed
export { Sentry };
